import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { listMyFams } from "../../lib/api";
import { useMySubscription } from "../../lib/api/subscription";
import { FamCard } from "../../components/FamCard";
import { CreateFamModal } from "../../components/CreateFamModal";
import { signOut } from "../../lib/auth";

// /app — dashboard. Lists the user's Fams (empty state + populated state),
// shows a Power Ranking placeholder, and a Fantasy section preview. Phase 1
// is read-only; vote UI is Phase 2.
export function DashboardRoute() {
	const navigate = useNavigate();
	const famsQuery = useQuery({
		queryKey: ["me", "fams"],
		queryFn: listMyFams,
	});
	// `status === "none"` is the explicit free-tier sentinel returned by
	// /api/me/subscription when the user has no row. Active/trialing
	// subscribers get the link hidden so we don't double-upsell them.
	const subQuery = useMySubscription();
	const isFreeTier =
		!subQuery.data ||
		subQuery.data.status === "none" ||
		subQuery.data.status === "canceled" ||
		subQuery.data.status === "incomplete_expired";
	const [createOpen, setCreateOpen] = useState(false);
	const famCount = famsQuery.data?.length ?? 0;
	// Free tier: 1 Personal + 1 Public = 2 fams max. Paid: more (handled
	// server-side; we just stop showing the CTA past 6 to avoid spam).
	const canCreateMore = isFreeTier ? famCount < 2 : famCount < 6;

	return (
		<div className="mx-auto max-w-5xl space-y-8 px-4 py-8">
			<header className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold">Your Hedge Fam</h1>
					<p className="text-sm text-[var(--color-muted)]">
						Manage your Fams, propose ideas, follow Fantasy.
					</p>
				</div>
				<div className="flex items-center gap-2">
					{isFreeTier && (
						<Link
							to="/upgrade"
							className="rounded-md border border-[var(--color-accent)] px-3 py-1.5 text-sm text-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-black"
						>
							Upgrade
						</Link>
					)}
					<Link
						to="/settings"
						className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
					>
						Settings
					</Link>
					<button
						onClick={() => {
							void signOut().then(() => {
								window.location.assign("/auth/login");
							});
						}}
						className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-danger)]"
					>
						Sign out
					</button>
				</div>
			</header>

			<section className="space-y-3">
				<div className="flex items-center justify-between">
					<h2 className="text-lg font-semibold">Fams</h2>
					{famsQuery.data && canCreateMore && (
						<button
							type="button"
							onClick={() => setCreateOpen(true)}
							className="rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-semibold text-black hover:opacity-90"
						>
							+ Create your Fam
						</button>
					)}
				</div>
				{famsQuery.isLoading && (
					<p className="text-sm text-[var(--color-muted)]">Loading…</p>
				)}
				{famsQuery.isError && (
					<p className="text-sm text-[var(--color-danger)]">
						Could not load Fams.{" "}
						{famsQuery.error instanceof Error ? famsQuery.error.message : ""}
					</p>
				)}
				{famsQuery.data?.length === 0 && (
					<div className="rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-bg-elev)] p-8 text-center">
						<p className="text-base font-semibold">Start your first Fam</p>
						<p className="mt-1 text-sm text-[var(--color-muted)]">
							Your Fam is your investing crew. Click{" "}
							<span className="font-semibold">Create your Fam</span> above
							to begin.
						</p>
					</div>
				)}
				{famsQuery.data && famsQuery.data.length > 0 && (
					<div className="grid gap-3 sm:grid-cols-2">
						{famsQuery.data.map((fam) => (
							<FamCard key={fam.id} fam={fam} />
						))}
					</div>
				)}
				{createOpen && (
					<CreateFamModal
						existingFamCount={famCount}
						isPaid={!isFreeTier}
						onClose={() => setCreateOpen(false)}
						onCreated={(fam) => {
							setCreateOpen(false);
							// Drop the user straight into their new Fam so they can
							// start configuring it (members, composite, etc.).
							if (fam.slug) {
								void navigate(`/fam/${fam.slug}`);
							}
						}}
					/>
				)}
			</section>

			<section className="space-y-3">
				<h2 className="text-lg font-semibold">Power Ranking</h2>
				<Link
					to="/power-ranking"
					className="block rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-6 transition hover:border-[var(--color-accent)]"
				>
					<p className="text-sm">
						See your current ELO rating, win/loss/draw record, and recent
						settlement deltas.
					</p>
					<p className="mt-1 text-xs text-[var(--color-muted)]">
						View Power Ranking →
					</p>
				</Link>
			</section>

			<section className="space-y-3">
				<h2 className="text-lg font-semibold">Fantasy</h2>
				<Link
					to="/fantasy"
					className="block rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-6 transition hover:border-[var(--color-accent)]"
				>
					<p className="text-sm">
						Four ways to compete: Weekly composites Mon → Fri, Quarterly vs
						SPY, Earnings Showdown weeks where reporters score 1.5×, and
						PM-vs-PM Duels.
					</p>
					<p className="mt-1 text-xs text-[var(--color-muted)]">
						Browse the leaderboards →
					</p>
				</Link>
			</section>
		</div>
	);
}
